/**
 * Value Object Identity - Représente une pièce d'identité
 *
 * Règles métier:
 * - Doit avoir un numéro unique
 * - Doit avoir des dates de validité cohérentes (émission < expiration)
 * - Ne doit pas être expirée pour les opérations sensibles
 * - Immutable
 */

import { isNotFutureDate, isNotExpired } from '../../shared/utils/validators';
import { InvalidIdentityDocumentError } from '../errors/DomainErrors';

export enum IdentityDocumentType {
  CNI = 'CNI',
  PASSPORT = 'PASSPORT',
  DRIVER_LICENSE = 'DRIVER_LICENSE',
  CONSULAR_CARD = 'CONSULAR_CARD',
  ATTESTATION = 'ATTESTATION',
}

export const IDENTITY_DOCUMENT_LABELS: Record<IdentityDocumentType, string> = {
  [IdentityDocumentType.CNI]: 'Carte Nationale d\'Identité',
  [IdentityDocumentType.PASSPORT]: 'Passeport',
  [IdentityDocumentType.DRIVER_LICENSE]: 'Permis de Conduire',
  [IdentityDocumentType.CONSULAR_CARD]: 'Carte Consulaire',
  [IdentityDocumentType.ATTESTATION]: 'Attestation d\'Identité',
};

export interface IdentityProps {
  readonly type: IdentityDocumentType;
  readonly number: string;
  readonly issueDate: Date;
  readonly expiryDate: Date;
  readonly issueCountry: string;
  readonly issuePlace: string;
  readonly holderName: string;
}

export class Identity {
  private constructor(private readonly props: IdentityProps) {
    this.validate();
    Object.freeze(this.props);
    Object.freeze(this);
  }

  /**
   * Crée une instance Identity avec validation
   */
  static create(props: IdentityProps): Identity {
    return new Identity(props);
  }

  /**
   * Valide les règles métier
   */
  private validate(): void {
    // Numéro requis
    if (!this.props.number || this.props.number.trim().length === 0) {
      throw new InvalidIdentityDocumentError('Le numéro de pièce est requis');
    }

    // Date d'émission ne doit pas être dans le futur
    if (!isNotFutureDate(this.props.issueDate)) {
      throw new InvalidIdentityDocumentError('La date d\'émission ne peut pas être dans le futur');
    }

    // Date d'expiration doit être après la date d'émission
    if (this.props.expiryDate <= this.props.issueDate) {
      throw new InvalidIdentityDocumentError(
        'La date d\'expiration doit être postérieure à la date d\'émission'
      );
    }

    // Pays et lieu d'émission optionnels (non stockés côté backend CBS)

    // Nom du titulaire requis
    if (!this.props.holderName || this.props.holderName.trim().length === 0) {
      throw new InvalidIdentityDocumentError('Le nom du titulaire est requis');
    }
  }

  // Getters
  get type(): IdentityDocumentType {
    return this.props.type;
  }

  get typeLabel(): string {
    return IDENTITY_DOCUMENT_LABELS[this.props.type];
  }

  get number(): string {
    return this.props.number;
  }

  get issueDate(): Date {
    return this.props.issueDate;
  }

  get expiryDate(): Date {
    return this.props.expiryDate;
  }

  get issueCountry(): string {
    return this.props.issueCountry;
  }

  get issuePlace(): string {
    return this.props.issuePlace;
  }

  get holderName(): string {
    return this.props.holderName;
  }

  /**
   * Vérifie si la pièce d'identité est expirée
   */
  isExpired(): boolean {
    return !isNotExpired(this.props.expiryDate);
  }

  /**
   * Vérifie si la pièce d'identité est valide (non expirée)
   */
  isValid(): boolean {
    return !this.isExpired();
  }

  /**
   * Calcule le nombre de jours avant expiration
   */
  getDaysUntilExpiry(): number {
    const now = new Date();
    const diffTime = this.props.expiryDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Vérifie si la pièce expire bientôt (moins de 30 jours)
   */
  isExpiringSoon(): boolean {
    return this.getDaysUntilExpiry() <= 30;
  }

  /**
   * Vérifie si la pièce correspond à un titulaire
   */
  belongsTo(clientName: string): boolean {
    return this.props.holderName.toLowerCase() === clientName.toLowerCase();
  }

  /**
   * Compare deux Identity
   */
  equals(other: Identity): boolean {
    return (
      this.props.type === other.props.type &&
      this.props.number === other.props.number
    );
  }

  /**
   * Sérialise pour persistence
   */
  toJSON(): Record<string, unknown> {
    return {
      type: this.props.type,
      number: this.props.number,
      issueDate: this.props.issueDate.toISOString(),
      expiryDate: this.props.expiryDate.toISOString(),
      issueCountry: this.props.issueCountry,
      issuePlace: this.props.issuePlace,
      holderName: this.props.holderName,
    };
  }

  /**
   * Désérialise depuis persistence
   */
  static fromJSON(json: Record<string, any>): Identity {
    return Identity.create({
      type: json.type as IdentityDocumentType,
      number: json.number,
      issueDate: new Date(json.issueDate),
      expiryDate: new Date(json.expiryDate),
      issueCountry: json.issueCountry,
      issuePlace: json.issuePlace,
      holderName: json.holderName,
    });
  }
}
